import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Loader2, Link2, Play, Pause, TrendingUp, 
  Target, Image, DollarSign, BarChart3, 
  CheckCircle2, XCircle, AlertCircle, Lock, Zap,
  ImageIcon, ChevronLeft, ChevronRight, RefreshCw,
  ArrowUpRight, ArrowDownRight, Minus, Eye, Globe,
  ChevronDown, ChevronUp, Layers, Users, FileImage,
  Smartphone, Monitor, Instagram, Facebook, Download, Sparkles,
  ExternalLink, HelpCircle
} from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import PerformanceCharts from './PerformanceCharts';

// Lead Ads TOS Guide Modal Component
const LeadAdsTOSGuide = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[rgba(255,255,255,0.05)] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-[rgba(255,255,255,0.05)] rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#F5F5F7]">Lead Ads freischalten</h2>
                <p className="text-[rgba(245,245,247,0.6)] text-sm">Einmalige Einrichtung erforderlich</p>
              </div>
            </div>
            <button onClick={onClose} className="text-[rgba(245,245,247,0.5)] hover:text-[rgba(245,245,247,0.7)] text-2xl">&times;</button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-blue-800 text-sm">
              <strong>Warum ist das nötig?</strong> Meta verlangt, dass du die Nutzungsbedingungen für Lead Ads 
              einmalig akzeptierst, bevor du Lead-Formulare nutzen kannst. Das geht leider nur direkt im Facebook Ads Manager.
            </p>
          </div>
          
          <div className="space-y-4">
            <h3 className="font-bold text-[#F5F5F7] flex items-center gap-2">
              <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm">1</span>
              Öffne den Facebook Ads Manager
            </h3>
            <p className="text-[rgba(245,245,247,0.7)] ml-8">
              Klicke auf den Button unten, um direkt zum Ads Manager zu gelangen.
            </p>
            <div className="ml-8">
              <a 
                href="https://www.facebook.com/ads/manager/creation" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Facebook className="w-4 h-4" />
                Ads Manager öffnen
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="font-bold text-[#F5F5F7] flex items-center gap-2">
              <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm">2</span>
              Erstelle eine neue Kampagne mit Ziel "Leads"
            </h3>
            <div className="ml-8 bg-[rgba(255,255,255,0.03)] rounded-xl p-4 space-y-3">
              <p className="text-[rgba(245,245,247,0.7)]">• Klicke auf <strong>"+ Erstellen"</strong></p>
              <p className="text-[rgba(245,245,247,0.7)]">• Wähle als Kampagnenziel <strong>"Leads"</strong></p>
              <p className="text-[rgba(245,245,247,0.7)]">• Klicke auf <strong>"Weiter"</strong></p>
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="font-bold text-[#F5F5F7] flex items-center gap-2">
              <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm">3</span>
              Akzeptiere die Lead Ads Nutzungsbedingungen
            </h3>
            <div className="ml-8 bg-[rgba(255,255,255,0.03)] rounded-xl p-4 space-y-3">
              <p className="text-[rgba(245,245,247,0.7)]">• Es erscheint ein <strong>Popup mit den Nutzungsbedingungen</strong></p>
              <p className="text-[rgba(245,245,247,0.7)]">• Lies die Bedingungen durch und klicke auf <strong>"Akzeptieren"</strong></p>
              <p className="text-[rgba(245,245,247,0.7)]">• Du musst die Kampagne <strong>nicht fertig erstellen</strong> – du kannst sie abbrechen</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="font-bold text-[#F5F5F7] flex items-center gap-2">
              <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm">✓</span>
              Fertig! Komm zurück zu FinAds
            </h3>
            <div className="ml-8 bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-green-800">
                Nachdem du die Nutzungsbedingungen akzeptiert hast, kannst du Lead Ads direkt über FinAds erstellen. 
                Du musst diesen Schritt <strong>nur einmal</strong> durchführen.
              </p>
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-200 bg-[rgba(255,255,255,0.03)] rounded-b-2xl flex gap-3">
          <Button onClick={onClose} variant="outline" className="flex-1">
            Später erledigen
          </Button>
          <a 
            href="https://www.facebook.com/ads/manager/creation" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex-1"
          >
            <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white">
              <Facebook className="w-4 h-4 mr-2" />
              Jetzt freischalten
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

const MetaAdsAutopilot = ({ isActive = false }) => {
  // State
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    meta_autopilot_unlocked: false
  });
  const [adAccounts, setAdAccounts] = useState([]);
  const [pages, setPages] = useState([]);
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedAdAccount, setSelectedAdAccount] = useState('');
  const [selectedPage, setSelectedPage] = useState('');
  const [selectedInstagram, setSelectedInstagram] = useState('');
  
  // Lead Ads TOS Guide
  const [showTOSGuide, setShowTOSGuide] = useState(false);
  
  // Campaign hierarchy view
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [expandedAdSets, setExpandedAdSets] = useState({});
  
  // Campaign creation state
  const [showCampaignWizard, setShowCampaignWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [campaignData, setCampaignData] = useState({
    name: '',
    objective: 'LEADS',
    budget_type: 'CBO',
    daily_budget: 20,
    targeting_type: 'bu',
    headline: '',
    primary_text: '',
    description: '',
    link_url: '',
    call_to_action: 'LEARN_MORE',
    image_id: '',
    conversion_type: 'lead_form',
    lead_form_questions: ['name', 'email', 'phone'],
    pixel_confirmed: false,
    conversion_event: 'Lead',
    placements: ['facebook_feed', 'instagram_feed', 'instagram_stories'],
    custom_targeting: null,
    use_custom_targeting: false,
    // Location targeting
    countries: ['DE'],
    // Page selection
    facebook_page_id: '',
    instagram_account_id: '',
    // Lead form settings - basic
    lead_form_headline: 'Kostenlose Beratung anfordern',
    lead_form_description: 'Füllen Sie das Formular aus und wir melden uns bei Ihnen.',
    lead_form_thank_you: 'Vielen Dank! Wir melden uns in Kürze bei Ihnen.',
    privacy_policy_url: '',
    website_url: '',  // User's website for Thank You page
    // FIX #2: Greeting/intro for lead form
    lead_form_greeting_headline: '',
    lead_form_greeting_description: '',
    // FIX #4: Custom thank you page (German)
    lead_form_thank_you_title: '',
    lead_form_thank_you_body: '',
    // FIX #5: Use creative as background
    use_creative_as_background: false,
    // Bid settings
    bid_cap: null  // Optional: max EUR per lead (null = automatic)
  });

  // Image gallery state
  const [userImages, setUserImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Saved AdCopies
  const [savedAdCopies, setSavedAdCopies] = useState([]);
  const [showAdCopySelector, setShowAdCopySelector] = useState(false);

  // Targeting suggestions
  const [targetingSuggestions, setTargetingSuggestions] = useState(null);
  const [savedTargeting, setSavedTargeting] = useState(null);

  // Ad Preview state
  const [showAdPreview, setShowAdPreview] = useState(false);
  const [previewPlatform, setPreviewPlatform] = useState('facebook_feed');

  // Dashboard state
  const [datePreset, setDatePreset] = useState('last_7d');
  const [aggregatedStats, setAggregatedStats] = useState({
    totalSpend: 0,
    totalReach: 0,
    totalClicks: 0,
    totalLeads: 0,
    avgCtr: 0,
    avgCpc: 0,
    avgCpl: 0
  });

  // Date preset options
  const datePresetOptions = [
    { value: 'today', label: 'Heute' },
    { value: 'yesterday', label: 'Gestern' },
    { value: 'last_7d', label: 'Letzte 7 Tage' },
    { value: 'last_14d', label: 'Letzte 14 Tage' },
    { value: 'last_30d', label: 'Letzte 30 Tage' },
    { value: 'this_month', label: 'Dieser Monat' },
    { value: 'last_month', label: 'Letzter Monat' }
  ];

  // Available countries
  const availableCountries = [
    { code: 'DE', name: 'Deutschland', flag: '🇩🇪' },
    { code: 'AT', name: 'Österreich', flag: '🇦🇹' },
    { code: 'CH', name: 'Schweiz', flag: '🇨🇭' },
    { code: 'NL', name: 'Niederlande', flag: '🇳🇱' },
    { code: 'BE', name: 'Belgien', flag: '🇧🇪' },
    { code: 'LU', name: 'Luxemburg', flag: '🇱🇺' },
    { code: 'FR', name: 'Frankreich', flag: '🇫🇷' },
    { code: 'IT', name: 'Italien', flag: '🇮🇹' },
    { code: 'ES', name: 'Spanien', flag: '🇪🇸' },
    { code: 'PL', name: 'Polen', flag: '🇵🇱' },
    { code: 'GB', name: 'Großbritannien', flag: '🇬🇧' },
    { code: 'US', name: 'USA', flag: '🇺🇸' }
  ];

  // Insurance types for targeting
  const insuranceTypes = [
    { value: 'bu', label: 'Berufsunfähigkeitsversicherung' },
    { value: 'kfz', label: 'KFZ-Versicherung' },
    { value: 'haftpflicht', label: 'Haftpflichtversicherung' },
    { value: 'leben', label: 'Lebensversicherung' },
    { value: 'rente', label: 'Rentenversicherung' },
    { value: 'kranken', label: 'Krankenversicherung' },
    { value: 'hausrat', label: 'Hausratversicherung' },
    { value: 'rechtsschutz', label: 'Rechtsschutzversicherung' }
  ];

  // Track if we've done the initial load
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [imagesLoadedOnce, setImagesLoadedOnce] = useState(false);

  // Load data when tab becomes active (not on initial mount)
  useEffect(() => {
    if (isActive && !initialLoadDone) {
      checkConnectionStatus();
      loadUserImages(false).then(() => setImagesLoadedOnce(true));
      loadSavedAdCopies();
      loadSavedTargeting();
      setInitialLoadDone(true);
    }
  }, [isActive, initialLoadDone]);

  // Listen for custom events when content is created in other tabs
  useEffect(() => {
    const handleContentCreated = (event) => {
      const { type } = event.detail || {};
      if (type === 'adcopy') {
        loadSavedAdCopies();
      } else if (type === 'image') {
        loadUserImages(false).then(() => setImagesLoadedOnce(true));
      } else if (type === 'targeting') {
        loadSavedTargeting();
      } else {
        // Refresh all if type not specified
        loadUserImages(false).then(() => setImagesLoadedOnce(true));
        loadSavedAdCopies();
        loadSavedTargeting();
      }
    };

    window.addEventListener('contentCreated', handleContentCreated);
    return () => window.removeEventListener('contentCreated', handleContentCreated);
  }, []);

  // Refresh all user content when wizard is opened (only if not already loaded)
  useEffect(() => {
    if (showCampaignWizard && !imagesLoadedOnce) {
      // Only load if we haven't loaded yet
      loadUserImages(false).then(() => setImagesLoadedOnce(true));
      loadSavedAdCopies();
      loadSavedTargeting();
    }
  }, [showCampaignWizard, imagesLoadedOnce]);

  const checkConnectionStatus = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/meta/status');
      setConnectionStatus(response.data);
      
      if (response.data.connected) {
        loadAdAccounts();
        loadPages();
        loadInstagramAccounts();
      }
      
      loadSavedTargeting();
    } catch (error) {
      console.error('Failed to check connection status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSavedTargeting = async () => {
    try {
      const response = await axiosInstance.get('/targeting/saved');
      if (response.data && response.data.targeting) {
        setSavedTargeting(response.data.targeting);
      }
    } catch (error) {
      console.log('No saved targeting found');
    }
  };

  const connectMeta = async () => {
    try {
      const response = await axiosInstance.get('/meta/auth/url');
      window.location.href = response.data.auth_url;
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Bitte erwerben Sie zuerst das Meta Ads Autopilot Feature');
      } else {
        toast.error('Fehler beim Verbinden mit Meta');
      }
    }
  };

  const loadSavedAdCopies = async () => {
    try {
      const response = await axiosInstance.get('/adcopy/saved?limit=10');
      setSavedAdCopies(response.data.adcopies || []);
    } catch (error) {
      console.log('Failed to load saved ad copies:', error);
    }
  };

  const applyAdCopy = (adcopy) => {
    // Parse the ad copy to extract headline and text
    const copyText = adcopy.ad_copy || '';
    
    // Try to extract headline (first line or text before first newline)
    const lines = copyText.split('\n').filter(l => l.trim());
    let headline = '';
    let primaryText = copyText;
    
    // Look for common patterns like "Headline:" or first short line
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.toLowerCase().includes('headline:') || line.toLowerCase().includes('überschrift:')) {
        headline = line.replace(/^(headline|überschrift):\s*/i, '');
        primaryText = lines.slice(i + 1).join('\n');
        break;
      } else if (i === 0 && line.length < 80) {
        // First short line could be headline
        headline = line;
        primaryText = lines.slice(1).join('\n');
        break;
      }
    }
    
    // If no headline found, use first 60 chars
    if (!headline && primaryText) {
      headline = primaryText.substring(0, 60).split('\n')[0];
    }
    
    setCampaignData(prev => ({
      ...prev,
      headline: headline || prev.headline,
      primary_text: primaryText || prev.primary_text
    }));
    
    setShowAdCopySelector(false);
    toast.success('AdCopy übernommen!');
  };

  const loadAdAccounts = async () => {
    try {
      const response = await axiosInstance.get('/meta/ad-accounts');
      setAdAccounts(response.data.ad_accounts || []);
      if (response.data.ad_accounts?.length > 0) {
        setSelectedAdAccount(response.data.ad_accounts[0].id);
        loadCampaignsWithHierarchy(response.data.ad_accounts[0].id);
      }
    } catch (error) {
      console.error('Failed to load ad accounts:', error);
    }
  };

  const loadPages = async () => {
    try {
      const response = await axiosInstance.get('/meta/pages');
      setPages(response.data.pages || []);
      if (response.data.pages?.length > 0) {
        setSelectedPage(response.data.pages[0].id);
        setCampaignData(prev => ({ ...prev, facebook_page_id: response.data.pages[0].id }));
      }
    } catch (error) {
      console.error('Failed to load pages:', error);
    }
  };

  const loadInstagramAccounts = async () => {
    try {
      const response = await axiosInstance.get('/meta/instagram-accounts');
      setInstagramAccounts(response.data.accounts || []);
      if (response.data.accounts?.length > 0) {
        setSelectedInstagram(response.data.accounts[0].id);
        setCampaignData(prev => ({ ...prev, instagram_account_id: response.data.accounts[0].id }));
      }
    } catch (error) {
      console.error('Failed to load Instagram accounts:', error);
    }
  };

  const loadCampaignsWithHierarchy = async (adAccountId, dateFilter = datePreset) => {
    try {
      const response = await axiosInstance.get(`/meta/campaigns/hierarchy?ad_account_id=${adAccountId}&date_preset=${dateFilter}`);
      const campaignData = response.data.campaigns || [];
      setCampaigns(campaignData);
      
      // Calculate aggregated statistics
      calculateAggregatedStats(campaignData);
    } catch (error) {
      // Fallback to simple campaigns list
      try {
        const response = await axiosInstance.get(`/meta/campaigns?ad_account_id=${adAccountId}`);
        const campaignData = response.data.campaigns || [];
        setCampaigns(campaignData);
        calculateAggregatedStats(campaignData);
      } catch (e) {
        console.error('Failed to load campaigns:', e);
      }
    }
  };

  // Calculate aggregated stats from campaigns
  const calculateAggregatedStats = (campaignList) => {
    let totalSpend = 0;
    let totalReach = 0;
    let totalClicks = 0;
    let totalLeads = 0;
    let totalImpressions = 0;
    let campaignsWithData = 0;

    campaignList.forEach(campaign => {
      const insights = campaign.insights || {};
      if (insights.spend || insights.reach || insights.clicks) {
        campaignsWithData++;
        totalSpend += parseFloat(insights.spend) || 0;
        totalReach += parseInt(insights.reach) || 0;
        totalClicks += parseInt(insights.clicks) || 0;
        totalLeads += parseInt(insights.leads) || 0;
        totalImpressions += parseInt(insights.impressions) || 0;
      }
    });

    const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const avgCpc = totalClicks > 0 ? totalSpend / totalClicks : 0;
    const avgCpl = totalLeads > 0 ? totalSpend / totalLeads : 0;

    setAggregatedStats({
      totalSpend,
      totalReach,
      totalClicks,
      totalLeads,
      avgCtr,
      avgCpc,
      avgCpl
    });
  };

  // Handle date preset change
  const handleDatePresetChange = (newPreset) => {
    setDatePreset(newPreset);
    if (selectedAdAccount) {
      loadCampaignsWithHierarchy(selectedAdAccount, newPreset);
    }
  };

  const loadUserImages = async (showError = true) => {
    try {
      setLoadingImages(true);
      const response = await axiosInstance.get('/images/gallery?limit=20');
      setUserImages(response.data.images || []);
    } catch (error) {
      console.error('Failed to load user images:', error);
      // Only show error toast if explicitly requested AND not a transient error
      const status = error.response?.status;
      const isTransientError = status === 401 || status === 520 || status === 502 || status === 503;
      if (showError && !isTransientError) {
        toast.error('Fehler beim Laden der Bilder');
      }
    } finally {
      setLoadingImages(false);
    }
  };

  const loadTargetingSuggestions = async (insuranceType) => {
    try {
      const response = await axiosInstance.get(`/meta/targeting-suggestions/${insuranceType}`);
      setTargetingSuggestions(response.data);
    } catch (error) {
      console.error('Failed to load targeting suggestions:', error);
    }
  };

  const handleAdAccountChange = (value) => {
    setSelectedAdAccount(value);
    loadCampaignsWithHierarchy(value);
  };

  const handleTargetingTypeChange = (value) => {
    setCampaignData({ ...campaignData, targeting_type: value });
    loadTargetingSuggestions(value);
  };

  const toggleCampaignExpand = (campaignId) => {
    setExpandedCampaigns(prev => ({
      ...prev,
      [campaignId]: !prev[campaignId]
    }));
  };

  const toggleAdSetExpand = (adSetId) => {
    setExpandedAdSets(prev => ({
      ...prev,
      [adSetId]: !prev[adSetId]
    }));
  };

  const toggleCampaignStatus = async (campaignId, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      await axiosInstance.patch(`/meta/campaigns/${campaignId}`, { status: newStatus });
      toast.success(newStatus === 'ACTIVE' ? 'Kampagne gestartet!' : 'Kampagne pausiert!');
      loadCampaignsWithHierarchy(selectedAdAccount);
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Kampagne');
    }
  };

  const createCampaign = async () => {
    if (!selectedAdAccount || !campaignData.facebook_page_id || !campaignData.name || !campaignData.headline) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    if (!campaignData.placements || campaignData.placements.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Platzierung aus');
      return;
    }

    // Validate website URL for lead forms
    if (campaignData.conversion_type === 'lead_form' && !campaignData.website_url) {
      toast.error('Bitte geben Sie Ihre Website-URL für die Danke-Seite an');
      return;
    }

    setCreatingCampaign(true);
    try {
      const response = await axiosInstance.post(`/meta/campaigns?ad_account_id=${selectedAdAccount}`, {
        ...campaignData,
        page_id: campaignData.facebook_page_id
      });
      
      toast.success('Kampagne erfolgreich erstellt!');
      setShowCampaignWizard(false);
      setWizardStep(1);
      resetCampaignData();
      loadCampaignsWithHierarchy(selectedAdAccount);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Kampagne');
    } finally {
      setCreatingCampaign(false);
    }
  };

  const resetCampaignData = () => {
    setCampaignData({
      name: '',
      objective: 'LEADS',
      budget_type: 'CBO',
      daily_budget: 20,
      targeting_type: 'bu',
      headline: '',
      primary_text: '',
      description: '',
      link_url: '',
      call_to_action: 'LEARN_MORE',
      image_id: '',
      conversion_type: 'lead_form',
      lead_form_questions: ['name', 'email', 'phone'],
      pixel_confirmed: false,
      conversion_event: 'Lead',
      placements: ['facebook_feed', 'instagram_feed', 'instagram_stories'],
      custom_targeting: null,
      use_custom_targeting: false,
      countries: ['DE'],
      facebook_page_id: selectedPage,
      instagram_account_id: selectedInstagram,
      website_url: '',
      privacy_policy_url: '',
      // Lead form settings
      lead_form_headline: 'Kostenlose Beratung anfordern',
      lead_form_description: 'Füllen Sie das Formular aus und wir melden uns bei Ihnen.',
      lead_form_thank_you: 'Vielen Dank! Wir melden uns in Kürze bei Ihnen.',
      // FIX #2: Greeting/intro
      lead_form_greeting_headline: '',
      lead_form_greeting_description: '',
      // FIX #4: Thank you page (German)
      lead_form_thank_you_title: '',
      lead_form_thank_you_body: '',
      // FIX #5: Use creative as background image
      use_creative_as_background: false
    });
    setSelectedImage(null);
  };

  const buyMetaAutopilot = () => {
    window.location.href = 'https://buy.stripe.com/bJe00k2JN73PdEJ1mo3F607';
  };

  // Performance rating helpers
  const getPerformanceRating = (metric, value) => {
    const thresholds = {
      ctr: { excellent: 2.0, good: 1.0, poor: 0.5 },
      cpc: { excellent: 0.5, good: 1.0, poor: 2.0 },
      cpm: { excellent: 5, good: 10, poor: 20 },
      cost_per_lead: { excellent: 5, good: 15, poor: 30 }
    };

    const t = thresholds[metric];
    if (!t) return 'neutral';

    if (metric === 'ctr') {
      if (value >= t.excellent) return 'excellent';
      if (value >= t.good) return 'good';
      if (value >= t.poor) return 'average';
      return 'poor';
    } else {
      if (value <= t.excellent) return 'excellent';
      if (value <= t.good) return 'good';
      if (value <= t.poor) return 'average';
      return 'poor';
    }
  };

  const getRatingColor = (rating) => {
    const colors = {
      excellent: 'bg-green-500 text-white',
      good: 'bg-green-400 text-white',
      average: 'bg-yellow-500 text-black',
      poor: 'bg-red-500 text-white',
      neutral: 'bg-gray-400 text-white'
    };
    return colors[rating] || colors.neutral;
  };

  // Ad Preview Component
  const AdPreview = () => {
    const currentPage = pages.find(p => p.id === campaignData.facebook_page_id);
    const pageName = currentPage?.name || 'Ihre Seite';
    
    return (
      <div className="border rounded-lg overflow-hidden bg-[rgba(255,255,255,0.05)] shadow-lg max-w-md mx-auto">
        {/* Preview Platform Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setPreviewPlatform('facebook_feed')}
            className={`flex-1 py-2 text-sm font-medium ${previewPlatform === 'facebook_feed' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-[rgba(245,245,247,0.6)]'}`}
          >
            <Facebook className="w-4 h-4 inline mr-1" /> Feed
          </button>
          <button
            onClick={() => setPreviewPlatform('instagram_feed')}
            className={`flex-1 py-2 text-sm font-medium ${previewPlatform === 'instagram_feed' ? 'bg-pink-50 text-pink-600 border-b-2 border-pink-600' : 'text-[rgba(245,245,247,0.6)]'}`}
          >
            <Instagram className="w-4 h-4 inline mr-1" /> Feed
          </button>
          <button
            onClick={() => setPreviewPlatform('stories')}
            className={`flex-1 py-2 text-sm font-medium ${previewPlatform === 'stories' ? 'bg-[rgba(255,92,0,0.1)] text-[#FF5C00] border-b-2 border-purple-600' : 'text-[rgba(245,245,247,0.6)]'}`}
          >
            <Smartphone className="w-4 h-4 inline mr-1" /> Story
          </button>
        </div>

        {/* Facebook/Instagram Feed Preview */}
        {(previewPlatform === 'facebook_feed' || previewPlatform === 'instagram_feed') && (
          <div className="p-0">
            {/* Header */}
            <div className="flex items-center gap-2 p-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                {pageName.charAt(0)}
              </div>
              <div>
                <p className="font-semibold text-sm">{pageName}</p>
                <p className="text-xs text-[rgba(245,245,247,0.6)]">Gesponsert · 🌐</p>
              </div>
            </div>
            
            {/* Primary Text */}
            <div className="px-3 pb-2">
              <p className="text-sm">{campaignData.primary_text || 'Ihr Anzeigentext erscheint hier...'}</p>
            </div>
            
            {/* Image */}
            <div className="aspect-square bg-gray-100 relative">
              {selectedImage ? (
                <img 
                  src={`data:image/png;base64,${selectedImage.image_data}`}
                  alt="Ad Preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[rgba(245,245,247,0.5)]">
                  <ImageIcon className="w-16 h-16" />
                </div>
              )}
            </div>
            
            {/* Link Preview */}
            <div className="p-3 bg-[rgba(255,255,255,0.03)] border-t">
              <p className="text-xs text-[rgba(245,245,247,0.6)] uppercase">
                {campaignData.conversion_type === 'lead_form' ? 'Lead-Formular' : campaignData.link_url || 'ihre-website.de'}
              </p>
              <p className="font-semibold text-sm">{campaignData.headline || 'Ihre Headline'}</p>
              {/* FIX #6: Show description, or auto-generate from primary text */}
              <p className="text-xs text-[rgba(245,245,247,0.7)]">
                {campaignData.description 
                  ? campaignData.description 
                  : campaignData.primary_text 
                    ? campaignData.primary_text.split('.')[0].substring(0, 100)
                    : 'Ihre Beschreibung erscheint hier...'}
              </p>
            </div>
            
            {/* CTA Button */}
            <div className="p-3 border-t">
              <button className="w-full py-2 bg-gray-100 rounded font-medium text-sm text-[rgba(245,245,247,0.9)]">
                {campaignData.call_to_action === 'LEARN_MORE' && 'Mehr erfahren'}
                {campaignData.call_to_action === 'SIGN_UP' && 'Registrieren'}
                {campaignData.call_to_action === 'CONTACT_US' && 'Kontaktieren'}
                {campaignData.call_to_action === 'GET_QUOTE' && 'Angebot erhalten'}
                {campaignData.call_to_action === 'BOOK_NOW' && 'Jetzt buchen'}
              </button>
            </div>
          </div>
        )}

        {/* Story Preview */}
        {previewPlatform === 'stories' && (
          <div className="aspect-[9/16] bg-black relative max-h-96 mx-auto">
            {selectedImage ? (
              <img 
                src={`data:image/png;base64,${selectedImage.image_data}`}
                alt="Story Preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[rgba(245,245,247,0.6)]">
                <ImageIcon className="w-16 h-16" />
              </div>
            )}
            
            {/* Story Header */}
            <div className="absolute top-4 left-4 right-4 flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                {pageName.charAt(0)}
              </div>
              <p className="text-white text-sm font-medium drop-shadow">{pageName}</p>
              <Badge className="bg-[rgba(255,255,255,0.05)]/20 text-white text-xs">Gesponsert</Badge>
            </div>
            
            {/* Story CTA */}
            <div className="absolute bottom-4 left-4 right-4">
              <div className="bg-[rgba(255,255,255,0.05)] rounded-lg p-3 text-center">
                <p className="font-semibold text-sm">{campaignData.headline || 'Ihre Headline'}</p>
                <button className="mt-2 w-full py-2 bg-blue-500 text-white rounded font-medium text-sm">
                  {campaignData.call_to_action === 'LEARN_MORE' && 'Mehr erfahren'}
                  {campaignData.call_to_action === 'SIGN_UP' && 'Registrieren'}
                  {campaignData.call_to_action === 'CONTACT_US' && 'Kontaktieren'}
                  {campaignData.call_to_action === 'GET_QUOTE' && 'Angebot erhalten'}
                  {campaignData.call_to_action === 'BOOK_NOW' && 'Jetzt buchen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="meta-ads-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF5C00]" />
      </div>
    );
  }

  // Not unlocked - show purchase card
  if (!connectionStatus.meta_autopilot_unlocked) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="meta-ads-purchase">
        <Card className="border-2 relative overflow-hidden" style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', borderColor: 'rgba(255, 92, 0, 0.3)' }}>
          <CardHeader className="text-center">
            <Badge className="mx-auto mb-4" style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)', color: '#00F2FF' }}>Vereinfachte Version</Badge>
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(255, 92, 0, 0.15)' }}>
              <Zap className="w-8 h-8 text-[#FF5C00]" />
            </div>
            <CardTitle className="text-2xl" style={{ color: '#F5F5F7' }}>Meta Ads Autopilot</CardTitle>
            <CardDescription className="text-lg" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>
              Die einfache Alternative zum komplizierten Business Manager
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-3 rounded-lg text-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)', border: '1px solid rgba(0, 242, 255, 0.3)' }}>
              <p className="text-sm" style={{ color: '#00F2FF' }}>
                <strong>Perfekt für Sie, wenn:</strong> Der Meta Business Manager zu kompliziert ist und Sie einfach nur Werbung schalten möchten.
              </p>
            </div>
            
            <div className="grid gap-3">
              {[
                'Kampagnen in 3 einfachen Schritten erstellen',
                'Vorschau Ihrer Anzeige vor dem Start',
                'KI-Targeting automatisch für Versicherungen',
                'Creatives direkt aus der App verwenden',
                'Übersichtliches Dashboard (nicht wie bei Meta!)',
                'Lead-Formulare automatisch erstellen',
                'Ampel-System für Performance-Bewertung',
                'Kampagnen mit einem Klick starten/stoppen'
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: '#00F2FF' }} />
                  <span style={{ color: '#F5F5F7' }}>{feature}</span>
                </div>
              ))}
            </div>
            
            <div className="text-center pt-4 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
              <div className="flex items-center justify-center gap-3 mb-2">
                <span className="text-lg" style={{ color: 'rgba(245, 245, 247, 0.5)', textDecoration: 'line-through' }}>999€</span>
                <span className="text-3xl font-bold" style={{ color: '#FF5C00' }}>299€ / 799€</span>
              </div>
              <p className="text-sm mb-4" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Flex (monatlich) oder Elite (Lifetime)</p>
              
              <Button 
                onClick={buyMetaAutopilot}
                className="w-full h-12 text-lg"
                style={{ background: 'linear-gradient(135deg, #FF5C00 0%, #FF8A00 100%)', color: 'white' }}
                data-testid="buy-meta-autopilot-btn"
              >
                <Zap className="w-5 h-5 mr-2" />
                Jetzt freischalten
              </Button>
              
              <p className="text-xs mt-3" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>
                Keine monatlichen Gebühren • Keine Agentur nötig
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not connected - show connect button
  if (!connectionStatus.connected) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="meta-ads-connect">
        <Card className="border-2" style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', borderColor: 'rgba(0, 242, 255, 0.3)' }}>
          <CardHeader className="text-center">
            <Badge className="mx-auto mb-4" style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)', color: '#00F2FF' }}>Vereinfachte Version</Badge>
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)' }}>
              <Link2 className="w-8 h-8" style={{ color: '#00F2FF' }} />
            </div>
            <CardTitle className="text-2xl" style={{ color: '#F5F5F7' }}>Mit Meta verbinden</CardTitle>
            <CardDescription style={{ color: 'rgba(245, 245, 247, 0.6)' }}>
              Verbinden Sie Ihren Meta Business Manager einmalig, um loszulegen
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <Button 
              onClick={connectMeta}
              className="h-12 px-8 text-lg"
              style={{ background: '#1877F2', color: 'white' }}
              data-testid="connect-meta-btn"
            >
              <Facebook className="w-5 h-5 mr-2" />
              Mit Facebook verbinden
            </Button>
            <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>
              Sie werden zu Facebook weitergeleitet, um die Verbindung zu autorisieren
            </p>
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)', border: '1px solid rgba(255, 92, 0, 0.3)' }}>
              <p className="text-xs" style={{ color: '#FF8A00' }}>
                <strong>Hinweis:</strong> Sie benötigen ein Facebook-Werbekonto. Falls Sie noch keins haben, können Sie es während der Verbindung erstellen.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="meta-ads-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-bold" style={{ color: '#F5F5F7' }}>Meta Ads Autopilot</h1>
            <Badge style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)', color: '#00F2FF' }}>Vereinfacht</Badge>
          </div>
          <p style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Die einfache Alternative zum Business Manager</p>
        </div>
        <Badge className="self-start md:self-auto" style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)', color: '#00F2FF' }}>
          <CheckCircle2 className="w-4 h-4 mr-1" />
          Meta verbunden
        </Badge>
      </div>

      {/* Account & Page Selection */}
      <Card className="p-4" style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <DollarSign className="w-4 h-4" /> Werbekonto
            </Label>
            <Select value={selectedAdAccount} onValueChange={handleAdAccountChange}>
              <SelectTrigger data-testid="ad-account-select">
                <SelectValue placeholder="Werbekonto wählen" />
              </SelectTrigger>
              <SelectContent>
                {adAccounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name} ({account.currency})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {adAccounts.length === 0 && (
              <div className="p-2 bg-amber-50 rounded-lg border border-amber-200 mt-2">
                <p className="text-xs text-amber-800 font-medium">💡 Kein Werbekonto gefunden?</p>
                <ul className="text-xs text-amber-700 mt-1 space-y-0.5 list-disc list-inside">
                  <li>Erstellen Sie ein Werbekonto im <a href="https://business.facebook.com/settings/ad-accounts" target="_blank" rel="noopener noreferrer" className="underline">Business Manager</a></li>
                  <li>Stellen Sie sicher, dass Sie Admin-Rechte haben</li>
                  <li>Verbinden Sie Meta erneut nach der Erstellung</li>
                </ul>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <Facebook className="w-4 h-4" /> Facebook Seite
            </Label>
            <Select value={selectedPage} onValueChange={(v) => { setSelectedPage(v); setCampaignData(prev => ({...prev, facebook_page_id: v})); }}>
              <SelectTrigger data-testid="page-select">
                <SelectValue placeholder="Seite wählen" />
              </SelectTrigger>
              <SelectContent>
                {pages.map((page) => (
                  <SelectItem key={page.id} value={page.id}>
                    {page.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {pages.length === 0 && (
              <div className="p-2 bg-amber-50 rounded-lg border border-amber-200 mt-2">
                <p className="text-xs text-amber-800 font-medium">💡 Keine Facebook-Seite gefunden?</p>
                <ul className="text-xs text-amber-700 mt-1 space-y-0.5 list-disc list-inside">
                  <li>Erstellen Sie eine <a href="https://www.facebook.com/pages/create" target="_blank" rel="noopener noreferrer" className="underline">Facebook-Seite</a> für Ihr Business</li>
                  <li>Bei der Meta-Verbindung: Alle Seiten freigeben</li>
                  <li>Sie müssen Admin der Seite sein</li>
                </ul>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <Instagram className="w-4 h-4" /> Instagram Account
            </Label>
            <Select value={selectedInstagram} onValueChange={(v) => { setSelectedInstagram(v); setCampaignData(prev => ({...prev, instagram_account_id: v})); }}>
              <SelectTrigger data-testid="instagram-select">
                <SelectValue placeholder="Instagram wählen (optional)" />
              </SelectTrigger>
              <SelectContent>
                {instagramAccounts.length > 0 ? (
                  instagramAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      @{account.username} ({account.followers_count} Follower)
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="none" disabled>Kein Instagram verbunden</SelectItem>
                )}
              </SelectContent>
            </Select>
            {instagramAccounts.length === 0 && (
              <div className="p-2 bg-amber-50 rounded-lg border border-amber-200 mt-2">
                <p className="text-xs text-amber-800 font-medium">💡 Kein Instagram-Account gefunden?</p>
                <ul className="text-xs text-amber-700 mt-1 space-y-0.5 list-disc list-inside">
                  <li>Instagram muss ein <strong>Business- oder Creator-Profil</strong> sein</li>
                  <li>Im Business Manager: <a href="https://business.facebook.com/settings/instagram-accounts" target="_blank" rel="noopener noreferrer" className="underline">Instagram verknüpfen</a></li>
                  <li>Instagram mit einer Facebook-Seite verbinden</li>
                  <li>Danach Meta-Verbindung erneuern</li>
                </ul>
              </div>
            )}
            {instagramAccounts.length > 0 && (
              <p className="text-xs text-green-600">✓ Instagram erfolgreich verbunden</p>
            )}
          </div>
        </div>
      </Card>

      {/* Create Campaign Button */}
      <Button 
        onClick={() => {
          setShowCampaignWizard(true);
          loadUserImages();
          loadSavedAdCopies();
        }}
        className="w-full h-12"
        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
        data-testid="create-campaign-btn"
      >
        <Zap className="w-5 h-5 mr-2" />
        Neue Kampagne erstellen (3 einfache Schritte)
      </Button>

      {/* Campaign Wizard Modal */}
      {showCampaignWizard && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowCampaignWizard(false);
                setSelectedImage(null);
                setShowAdPreview(false);
                setWizardStep(1);
              }}
              className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-[rgba(245,245,247,0.7)] hover:text-[#F5F5F7] transition-colors"
              aria-label="Schließen"
            >
              <XCircle className="w-5 h-5" />
            </button>
            
            <CardHeader>
              <div className="flex items-center justify-between pr-10">
                <div>
                  <CardTitle>Neue Kampagne erstellen</CardTitle>
                  <CardDescription>Schritt {wizardStep} von 3 - Einfacher als im Business Manager!</CardDescription>
                </div>
                {wizardStep >= 2 && selectedImage && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAdPreview(!showAdPreview)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    {showAdPreview ? 'Vorschau ausblenden' : 'Vorschau anzeigen'}
                  </Button>
                )}
              </div>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3].map((step) => (
                  <div 
                    key={step}
                    className={`h-2 flex-1 rounded ${step <= wizardStep ? 'bg-purple-600' : 'bg-gray-200'}`}
                  />
                ))}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className={`grid ${showAdPreview && wizardStep >= 2 ? 'md:grid-cols-2 gap-6' : ''}`}>
                {/* Left side - Form */}
                <div className="space-y-4">
                  {/* Step 1: Basic Info */}
                  {wizardStep === 1 && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Kampagnenname *</Label>
                        <Input 
                          placeholder="z.B. BU-Versicherung Leads Januar"
                          value={campaignData.name}
                          onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                          data-testid="campaign-name-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Versicherungsart (für KI-Targeting) *</Label>
                        <Select 
                          value={campaignData.targeting_type} 
                          onValueChange={handleTargetingTypeChange}
                        >
                          <SelectTrigger data-testid="targeting-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {insuranceTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {targetingSuggestions && (
                          <div className="mt-2 p-3 bg-[rgba(255,92,0,0.1)] rounded-lg text-sm">
                            <p className="font-medium text-purple-700 mb-1">🤖 KI-Targeting Vorschlag:</p>
                            <p>👥 {targetingSuggestions.age_range}</p>
                            <p>🎯 {targetingSuggestions.interests?.slice(0, 3).join(', ')}</p>
                          </div>
                        )}
                      </div>

                      {/* Saved Targeting from Targeting Helper */}
                      {savedTargeting && (
                        <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border-2 border-indigo-200">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-5 h-5 text-indigo-600" />
                              <div>
                                <p className="font-semibold text-indigo-900">Gespeichertes Targeting verfügbar</p>
                                <p className="text-xs text-indigo-700">
                                  Erstellt am {new Date(savedTargeting.generated_at).toLocaleDateString('de-DE')} 
                                  {savedTargeting.product_type && ` • ${savedTargeting.product_type.toUpperCase()}`}
                                </p>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant={campaignData.use_custom_targeting ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => {
                                if (campaignData.use_custom_targeting) {
                                  // Deactivate
                                  setCampaignData({ 
                                    ...campaignData, 
                                    use_custom_targeting: false,
                                    custom_targeting: null 
                                  });
                                  toast.info('Gespeichertes Targeting deaktiviert');
                                } else {
                                  // Activate
                                  setCampaignData({ 
                                    ...campaignData, 
                                    use_custom_targeting: true,
                                    custom_targeting: savedTargeting 
                                  });
                                  toast.success('Gespeichertes Targeting wird verwendet!');
                                }
                              }}
                              className={campaignData.use_custom_targeting 
                                ? 'bg-indigo-600 hover:bg-indigo-700' 
                                : 'border-indigo-300 text-indigo-700 hover:bg-indigo-100'
                              }
                              data-testid="use-saved-targeting-btn"
                            >
                              {campaignData.use_custom_targeting ? (
                                <>
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Aktiv
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-1" />
                                  Verwenden
                                </>
                              )}
                            </Button>
                          </div>
                          {campaignData.use_custom_targeting && savedTargeting.summary && (
                            <div className="mt-3 p-3 bg-[rgba(255,255,255,0.05)] rounded-lg text-sm">
                              <p className="text-[rgba(245,245,247,0.9)]">{savedTargeting.summary}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Country Selection */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          <Globe className="w-4 h-4" /> Länder/Standorte *
                        </Label>
                        <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto p-1">
                          {availableCountries.map((country) => (
                            <label key={country.code} className="flex items-center gap-2 p-2 bg-[rgba(255,255,255,0.03)] rounded cursor-pointer hover:bg-gray-100">
                              <input
                                type="checkbox"
                                checked={campaignData.countries?.includes(country.code)}
                                onChange={(e) => {
                                  const newCountries = e.target.checked
                                    ? [...(campaignData.countries || []), country.code]
                                    : (campaignData.countries || []).filter(c => c !== country.code);
                                  setCampaignData({ ...campaignData, countries: newCountries });
                                }}
                                className="rounded"
                              />
                              <span className="text-sm">{country.flag} {country.name}</span>
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-[rgba(245,245,247,0.6)]">
                          Ausgewählt: {campaignData.countries?.map(c => availableCountries.find(ac => ac.code === c)?.flag).join(' ') || 'Keine'}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>Budget-Strategie</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            type="button"
                            variant={campaignData.budget_type === 'CBO' ? 'default' : 'outline'}
                            onClick={() => setCampaignData({ ...campaignData, budget_type: 'CBO' })}
                            className="h-auto py-3"
                          >
                            <div className="text-left">
                              <p className="font-medium">CBO (Empfohlen)</p>
                              <p className="text-xs opacity-80">Meta verteilt automatisch</p>
                            </div>
                          </Button>
                          <Button
                            type="button"
                            variant={campaignData.budget_type === 'ABO' ? 'default' : 'outline'}
                            onClick={() => setCampaignData({ ...campaignData, budget_type: 'ABO' })}
                            className="h-auto py-3"
                          >
                            <div className="text-left">
                              <p className="font-medium">ABO</p>
                              <p className="text-xs opacity-80">Manuell steuern</p>
                            </div>
                          </Button>
                        </div>
                        <p className="text-xs text-blue-600 mt-1">
                          💡 <strong>Tipp:</strong> CBO ist ideal für Einsteiger - Meta optimiert automatisch für beste Ergebnisse.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>Tagesbudget: {campaignData.daily_budget}€</Label>
                        <Slider
                          value={[campaignData.daily_budget]}
                          onValueChange={([value]) => setCampaignData({ ...campaignData, daily_budget: value })}
                          min={5}
                          max={500}
                          step={5}
                        />
                        <div className="flex justify-between text-xs text-[rgba(245,245,247,0.6)]">
                          <span>5€</span>
                          <span>500€</span>
                        </div>
                        <p className="text-xs text-blue-600">
                          💡 <strong>Tipp:</strong> Starten Sie mit 10-20€/Tag. Nach 3-5 Tagen haben Sie genug Daten zur Optimierung.
                        </p>
                      </div>

                      {/* Placements */}
                      <div className="space-y-2">
                        <Label>Wo soll Ihre Anzeige erscheinen?</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { id: 'facebook_feed', label: 'Facebook Feed', icon: '📰' },
                            { id: 'facebook_stories', label: 'Facebook Stories', icon: '📱' },
                            { id: 'instagram_feed', label: 'Instagram Feed', icon: '📷' },
                            { id: 'instagram_stories', label: 'Instagram Stories', icon: '📲' },
                            { id: 'instagram_reels', label: 'Instagram Reels', icon: '🎥' },
                            { id: 'instagram_explore', label: 'Instagram Explore', icon: '🔍' }
                          ].map((placement) => (
                            <label key={placement.id} className="flex items-center gap-2 p-2 bg-[rgba(255,255,255,0.03)] rounded cursor-pointer hover:bg-gray-100">
                              <input
                                type="checkbox"
                                checked={campaignData.placements?.includes(placement.id)}
                                onChange={(e) => {
                                  const newPlacements = e.target.checked
                                    ? [...(campaignData.placements || []), placement.id]
                                    : (campaignData.placements || []).filter(p => p !== placement.id);
                                  setCampaignData({ ...campaignData, placements: newPlacements });
                                }}
                                className="rounded"
                              />
                              <span className="text-sm">{placement.icon} {placement.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Ad Creative */}
                  {wizardStep === 2 && (
                    <div className="space-y-4">
                      {/* Image Selection */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Anzeigenbild auswählen *</Label>
                          <Button variant="ghost" size="sm" onClick={() => loadUserImages(true)} disabled={loadingImages}>
                            <RefreshCw className={`w-4 h-4 mr-1 ${loadingImages ? 'animate-spin' : ''}`} />
                            Aktualisieren
                          </Button>
                        </div>
                        
                        {loadingImages ? (
                          <div className="flex items-center justify-center h-32 bg-[rgba(255,255,255,0.03)] rounded-lg">
                            <Loader2 className="w-6 h-6 animate-spin text-[#FF5C00]" />
                          </div>
                        ) : userImages.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-32 bg-[rgba(255,255,255,0.03)] rounded-lg border-2 border-dashed">
                            <ImageIcon className="w-8 h-8 text-[rgba(245,245,247,0.5)] mb-2" />
                            <p className="text-sm text-[rgba(245,245,247,0.6)]">Keine Bilder vorhanden</p>
                            <p className="text-xs text-[rgba(245,245,247,0.5)]">Erstellen Sie zuerst Bilder im Ad Creatives Tab</p>
                            <a href="#" onClick={(e) => { e.preventDefault(); setShowCampaignWizard(false); }} className="text-xs text-[#FF5C00] underline mt-1 inline-block">
                              → Zum Ad Creatives Tab
                            </a>
                          </div>
                        ) : (
                          <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto p-1">
                            {userImages.map((img) => (
                              <div
                                key={img.id}
                                onClick={() => {
                                  setSelectedImage(img);
                                  setCampaignData({ ...campaignData, image_id: img.id });
                                }}
                                className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                                  selectedImage?.id === img.id 
                                    ? 'border-purple-600 ring-2 ring-purple-300' 
                                    : 'border-transparent hover:border-purple-300'
                                }`}
                              >
                                <img 
                                  src={`data:image/png;base64,${img.image_data}`}
                                  alt={img.prompt}
                                  className="w-full h-full object-cover"
                                />
                                {selectedImage?.id === img.id && (
                                  <div className="absolute top-1 right-1">
                                    <CheckCircle2 className="w-5 h-5 text-[#FF5C00] bg-[rgba(255,255,255,0.05)] rounded-full" />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {userImages.length > 0 && (
                          <p className="text-xs text-blue-600">
                            💡 <strong>Tipp:</strong> Quadratische Bilder (1:1) funktionieren am besten für Feed-Anzeigen.
                          </p>
                        )}
                      </div>

                      {/* Lead Type Selection */}
                      <div className="space-y-2">
                        <Label>Wohin sollen Leads gehen? *</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            type="button"
                            variant={campaignData.conversion_type === 'lead_form' ? 'default' : 'outline'}
                            onClick={() => setCampaignData({ ...campaignData, conversion_type: 'lead_form', link_url: '' })}
                            className="h-auto py-3"
                          >
                            <div className="text-left">
                              <p className="font-medium">📋 Lead-Formular</p>
                              <p className="text-xs opacity-80">Direkt in Facebook/Instagram</p>
                            </div>
                          </Button>
                          <Button
                            type="button"
                            variant={campaignData.conversion_type === 'website' ? 'default' : 'outline'}
                            onClick={() => setCampaignData({ ...campaignData, conversion_type: 'website' })}
                            className="h-auto py-3"
                          >
                            <div className="text-left">
                              <p className="font-medium">🌐 Website</p>
                              <p className="text-xs opacity-80">Externe Landingpage</p>
                            </div>
                          </Button>
                        </div>
                        <p className="text-xs text-blue-600">
                          💡 <strong>Empfehlung:</strong> Lead-Formulare haben meist 2-3x höhere Conversion-Raten, da Nutzer Facebook/Instagram nicht verlassen müssen.
                        </p>
                      </div>

                      {/* Lead Form Questions */}
                      {campaignData.conversion_type === 'lead_form' && (
                        <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <Label className="text-blue-700 font-medium">📋 Lead-Formular Einstellungen</Label>
                          
                          <div className="space-y-2">
                            <Label className="text-sm text-blue-600">Welche Daten sollen abgefragt werden?</Label>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { id: 'name', label: 'Name', icon: '👤' },
                                { id: 'email', label: 'E-Mail', icon: '📧' },
                                { id: 'phone', label: 'Telefon', icon: '📱' }
                              ].map((field) => (
                                <label key={field.id} className="flex items-center gap-1 bg-[rgba(255,255,255,0.05)] px-2 py-1 rounded border cursor-pointer hover:bg-blue-100">
                                  <input
                                    type="checkbox"
                                    checked={campaignData.lead_form_questions.includes(field.id)}
                                    onChange={(e) => {
                                      const questions = e.target.checked
                                        ? [...campaignData.lead_form_questions, field.id]
                                        : campaignData.lead_form_questions.filter(q => q !== field.id);
                                      setCampaignData({ ...campaignData, lead_form_questions: questions });
                                    }}
                                    className="rounded"
                                  />
                                  <span className="text-sm">{field.icon} {field.label}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          
                          {/* FIX #2: Greeting/Intro Section */}
                          <div className="p-2 bg-[rgba(255,92,0,0.1)] rounded border border-[rgba(255,92,0,0.3)]">
                            <Label className="text-sm text-purple-700 font-semibold flex items-center gap-1">
                              👋 Begrüßung (Intro-Bereich)
                            </Label>
                            <p className="text-xs text-[#FF5C00] mb-2">Wird oben im Formular angezeigt, bevor die Fragen kommen</p>
                            
                            <div className="space-y-2">
                              <Input 
                                placeholder="z.B. Willkommen bei Ihrem Versicherungsberater"
                                value={campaignData.lead_form_greeting_headline}
                                onChange={(e) => setCampaignData({ ...campaignData, lead_form_greeting_headline: e.target.value })}
                                className="bg-[rgba(255,255,255,0.05)] text-sm"
                              />
                              <Textarea 
                                placeholder="z.B. Füllen Sie das kurze Formular aus und erhalten Sie innerhalb von 24 Stunden eine persönliche Beratung."
                                value={campaignData.lead_form_greeting_description}
                                onChange={(e) => setCampaignData({ ...campaignData, lead_form_greeting_description: e.target.value })}
                                rows={2}
                                className="bg-[rgba(255,255,255,0.05)] text-sm"
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-sm text-blue-600">Formular-Überschrift</Label>
                            <Input 
                              placeholder="z.B. Kostenlose Beratung anfordern"
                              value={campaignData.lead_form_headline}
                              onChange={(e) => setCampaignData({ ...campaignData, lead_form_headline: e.target.value })}
                              className="bg-[rgba(255,255,255,0.05)]"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-sm text-blue-600">Beschreibungstext</Label>
                            <Textarea 
                              placeholder="Kurze Beschreibung, warum der Nutzer das Formular ausfüllen soll..."
                              value={campaignData.lead_form_description}
                              onChange={(e) => setCampaignData({ ...campaignData, lead_form_description: e.target.value })}
                              rows={2}
                              className="bg-[rgba(255,255,255,0.05)]"
                            />
                          </div>
                          
                          {/* FIX #5: Background Image - Use Creative Image */}
                          <div className="p-2 bg-indigo-50 rounded border border-indigo-200">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={campaignData.use_creative_as_background}
                                onChange={(e) => setCampaignData({ ...campaignData, use_creative_as_background: e.target.checked })}
                                className="rounded"
                              />
                              <span className="text-sm text-indigo-700 font-semibold flex items-center gap-1">
                                🖼️ Hintergrundbild aktivieren
                              </span>
                            </label>
                            <p className="text-xs text-indigo-600 mt-1 ml-6">
                              Verwendet automatisch das ausgewählte Anzeigenbild als Hintergrundbild im Formular-Header
                            </p>
                          </div>
                          
                          {/* FIX #4: Thank You Page (German) */}
                          <div className="p-2 bg-green-50 rounded border border-green-200">
                            <Label className="text-sm text-green-700 font-semibold flex items-center gap-1">
                              ✅ Danke-Seite anpassen
                            </Label>
                            <p className="text-xs text-green-600 mb-2">Wird nach dem Absenden des Formulars angezeigt</p>
                            
                            <div className="space-y-2">
                              <Input 
                                placeholder="z.B. Vielen Dank für Ihr Interesse!"
                                value={campaignData.lead_form_thank_you_title}
                                onChange={(e) => setCampaignData({ ...campaignData, lead_form_thank_you_title: e.target.value })}
                                className="bg-[rgba(255,255,255,0.05)] text-sm"
                              />
                              <Textarea 
                                placeholder="z.B. Wir haben Ihre Anfrage erhalten. Ein Berater wird sich innerhalb von 24 Stunden persönlich bei Ihnen melden."
                                value={campaignData.lead_form_thank_you_body}
                                onChange={(e) => setCampaignData({ ...campaignData, lead_form_thank_you_body: e.target.value })}
                                rows={2}
                                className="bg-[rgba(255,255,255,0.05)] text-sm"
                              />
                            </div>
                            <p className="text-xs text-green-600 mt-1">
                              💡 Wenn leer gelassen, wird automatisch ein passender Text basierend auf Ihrer Ad-Copy erstellt.
                            </p>
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-sm text-blue-600">Datenschutzerklärung URL (optional)</Label>
                            <div className="flex">
                              <span className="inline-flex items-center px-3 text-sm text-[rgba(245,245,247,0.7)] bg-gray-100 border border-r-0 border-gray-300 rounded-l-md">
                                https://
                              </span>
                              <Input 
                                placeholder="ihre-website.de/datenschutz"
                                value={campaignData.privacy_policy_url.replace(/^https?:\/\//, '')}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/^https?:\/\//, '');
                                  setCampaignData({ ...campaignData, privacy_policy_url: value ? `https://${value}` : '' });
                                }}
                                className="bg-[rgba(255,255,255,0.05)] rounded-l-none"
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-sm text-orange-600 font-semibold">Website-URL für Danke-Seite *</Label>
                            <div className="flex">
                              <span className="inline-flex items-center px-3 text-sm text-[rgba(245,245,247,0.7)] bg-orange-100 border border-r-0 border-orange-300 rounded-l-md font-medium">
                                https://
                              </span>
                              <Input 
                                placeholder="ihre-website.de"
                                value={campaignData.website_url.replace(/^https?:\/\//, '')}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/^https?:\/\//, '');
                                  setCampaignData({ ...campaignData, website_url: value ? `https://${value}` : '' });
                                }}
                                className="bg-[rgba(255,255,255,0.05)] border-orange-300 rounded-l-none"
                                required
                              />
                            </div>
                            <p className="text-xs text-orange-600">
                              ⚠️ Pflichtfeld: Nach dem Absenden des Formulars wird der Lead auf diese Seite weitergeleitet.
                            </p>
                          </div>
                          
                          <p className="text-xs text-blue-600 mt-2">
                            ✅ Das Lead-Formular wird automatisch bei Meta erstellt
                          </p>
                        </div>
                      )}

                      {/* Website URL */}
                      {campaignData.conversion_type === 'website' && (
                        <div className="space-y-2">
                          <Label>Website-URL *</Label>
                          <Input 
                            placeholder="https://ihre-website.de/landingpage"
                            value={campaignData.link_url}
                            onChange={(e) => setCampaignData({ ...campaignData, link_url: e.target.value })}
                          />
                        </div>
                      )}

                      {/* AdCopy Selection */}
                      {savedAdCopies.length > 0 && (
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-green-700 font-medium">📝 Gespeicherte AdCopies verwenden</Label>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setShowAdCopySelector(!showAdCopySelector)}
                              className="text-green-700 border-green-300"
                            >
                              {showAdCopySelector ? 'Ausblenden' : 'Anzeigen'}
                            </Button>
                          </div>
                          
                          {showAdCopySelector && (
                            <div className="space-y-2 mt-2 max-h-48 overflow-y-auto">
                              {savedAdCopies.map((adcopy, index) => (
                                <div 
                                  key={adcopy.id || index}
                                  onClick={() => applyAdCopy(adcopy)}
                                  className="p-2 bg-[rgba(255,255,255,0.05)] rounded border cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors"
                                >
                                  <p className="text-sm font-medium truncate">
                                    {adcopy.ad_copy?.substring(0, 80)}...
                                  </p>
                                  <p className="text-xs text-[rgba(245,245,247,0.6)]">
                                    {adcopy.style} • {new Date(adcopy.created_at).toLocaleDateString('de-DE')}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <p className="text-xs text-green-600 mt-2">
                            Klicken Sie auf eine AdCopy, um Headline und Text zu übernehmen
                          </p>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label>Headline (Überschrift) *</Label>
                        <Input 
                          placeholder="z.B. Jetzt absichern - Kostenlose Beratung"
                          value={campaignData.headline}
                          onChange={(e) => setCampaignData({ ...campaignData, headline: e.target.value })}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Anzeigentext *</Label>
                        <Textarea 
                          placeholder="Der Haupttext Ihrer Anzeige... Beschreiben Sie Ihr Angebot."
                          value={campaignData.primary_text}
                          onChange={(e) => setCampaignData({ ...campaignData, primary_text: e.target.value })}
                          rows={3}
                        />
                      </div>

                      {/* FIX #6: Separate description field for ad */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          Beschreibung (Link-Beschreibung)
                          <span className="text-xs text-[rgba(245,245,247,0.5)] font-normal">optional</span>
                        </Label>
                        <Input 
                          placeholder="z.B. Experten-Beratung für Ihre finanzielle Zukunft"
                          value={campaignData.description}
                          onChange={(e) => setCampaignData({ ...campaignData, description: e.target.value })}
                        />
                        <p className="text-xs text-blue-600">
                          💡 Wird unter der Headline angezeigt. Wenn leer, wird automatisch ein passender Text aus Ihrem Anzeigentext generiert.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>Call-to-Action Button</Label>
                        <Select 
                          value={campaignData.call_to_action}
                          onValueChange={(value) => setCampaignData({ ...campaignData, call_to_action: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="LEARN_MORE">Mehr erfahren</SelectItem>
                            <SelectItem value="SIGN_UP">Registrieren</SelectItem>
                            <SelectItem value="CONTACT_US">Kontaktieren</SelectItem>
                            <SelectItem value="GET_QUOTE">Angebot erhalten</SelectItem>
                            <SelectItem value="BOOK_NOW">Jetzt buchen</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Review */}
                  {wizardStep === 3 && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg">Zusammenfassung</h3>
                      
                      <div className="space-y-3 bg-[rgba(255,255,255,0.03)] p-4 rounded-lg">
                        <div className="flex justify-between">
                          <span className="text-[rgba(245,245,247,0.6)]">Kampagne:</span>
                          <span className="font-medium">{campaignData.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[rgba(245,245,247,0.6)]">Länder:</span>
                          <span className="font-medium">
                            {campaignData.countries?.map(c => availableCountries.find(ac => ac.code === c)?.flag).join(' ')}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[rgba(245,245,247,0.6)]">Tagesbudget:</span>
                          <span className="font-medium">{campaignData.daily_budget}€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[rgba(245,245,247,0.6)]">Lead-Ziel:</span>
                          <span className="font-medium">
                            {campaignData.conversion_type === 'lead_form' ? '📋 Lead-Formular' : '🌐 Website'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[rgba(245,245,247,0.6)]">Platzierungen:</span>
                          <span className="font-medium">{campaignData.placements?.length || 0} ausgewählt</span>
                        </div>
                      </div>

                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                          ⚠️ Die Kampagne wird pausiert erstellt. Sie können sie nach Überprüfung aktivieren.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right side - Ad Preview (Step 2 & 3) */}
                {showAdPreview && wizardStep >= 2 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Eye className="w-4 h-4" /> Live-Vorschau Ihrer Anzeige
                    </Label>
                    <AdPreview />
                  </div>
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="flex gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    if (wizardStep === 1) {
                      setShowCampaignWizard(false);
                      setSelectedImage(null);
                      setShowAdPreview(false);
                    } else {
                      setWizardStep(wizardStep - 1);
                    }
                  }}
                  className="flex-1"
                >
                  {wizardStep === 1 ? 'Abbrechen' : 'Zurück'}
                </Button>
                <Button 
                  onClick={() => {
                    if (wizardStep === 3) {
                      createCampaign();
                    } else {
                      setWizardStep(wizardStep + 1);
                      if (wizardStep === 1) {
                        setShowAdPreview(true);
                      }
                    }
                  }}
                  disabled={
                    creatingCampaign || 
                    (wizardStep === 1 && (!campaignData.name || !campaignData.countries?.length)) ||
                    (wizardStep === 2 && !selectedImage) ||
                    (wizardStep === 2 && !campaignData.headline) ||
                    (wizardStep === 2 && campaignData.conversion_type === 'website' && !campaignData.link_url)
                  }
                  className="flex-1"
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                  {creatingCampaign ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : wizardStep === 3 ? (
                    'Kampagne erstellen'
                  ) : (
                    'Weiter'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Campaigns Dashboard with Hierarchy */}
      <div className="space-y-4">
        {/* Dashboard Header with Date Filter */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h2 className="text-xl font-semibold">Ihre Kampagnen</h2>
          <div className="flex items-center gap-3">
            {/* Date Filter */}
            <Select value={datePreset} onValueChange={handleDatePresetChange}>
              <SelectTrigger className="w-[180px]" data-testid="date-filter">
                <SelectValue placeholder="Zeitraum wählen" />
              </SelectTrigger>
              <SelectContent>
                {datePresetOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => loadCampaignsWithHierarchy(selectedAdAccount)}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Aktualisieren
            </Button>
          </div>
        </div>

        {/* Aggregated Statistics Cards */}
        {campaigns.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="stats-cards">
            <Card className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <p className="text-xs text-blue-600 font-medium">Ausgaben</p>
              <p className="text-xl font-bold text-blue-900">{aggregatedStats.totalSpend.toFixed(2)}€</p>
            </Card>
            <Card className="p-3 bg-gradient-to-br from-purple-50 to-purple-100 border-[rgba(255,92,0,0.3)]">
              <p className="text-xs text-[#FF5C00] font-medium">Reichweite</p>
              <p className="text-xl font-bold text-purple-900">{aggregatedStats.totalReach.toLocaleString()}</p>
            </Card>
            <Card className="p-3 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <p className="text-xs text-green-600 font-medium">Klicks</p>
              <p className="text-xl font-bold text-green-900">{aggregatedStats.totalClicks.toLocaleString()}</p>
            </Card>
            <Card className="p-3 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
              <p className="text-xs text-yellow-700 font-medium">Leads</p>
              <p className="text-xl font-bold text-yellow-900">{aggregatedStats.totalLeads}</p>
            </Card>
            <Card className={`p-3 ${getRatingColor(getPerformanceRating('ctr', aggregatedStats.avgCtr))} bg-opacity-20`}>
              <p className="text-xs font-medium opacity-80">Ø CTR</p>
              <p className="text-xl font-bold">{aggregatedStats.avgCtr.toFixed(2)}%</p>
            </Card>
            <Card className={`p-3 ${getRatingColor(getPerformanceRating('cpc', aggregatedStats.avgCpc))} bg-opacity-20`}>
              <p className="text-xs font-medium opacity-80">Ø CPC</p>
              <p className="text-xl font-bold">{aggregatedStats.avgCpc.toFixed(2)}€</p>
            </Card>
            <Card className={`p-3 ${getRatingColor(getPerformanceRating('cost_per_lead', aggregatedStats.avgCpl))} bg-opacity-20`}>
              <p className="text-xs font-medium opacity-80">Ø CPL</p>
              <p className="text-xl font-bold">{aggregatedStats.avgCpl.toFixed(2)}€</p>
            </Card>
          </div>
        )}

        {/* Performance Charts */}
        {selectedAdAccount && campaigns.length > 0 && (
          <PerformanceCharts adAccountId={selectedAdAccount} datePreset={datePreset} />
        )}
        
        {campaigns.length === 0 ? (
          <Card className="p-8 text-center text-[rgba(245,245,247,0.6)]">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">Noch keine Kampagnen vorhanden</p>
            <p className="text-sm">Erstellen Sie Ihre erste Kampagne mit dem Button oben</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {campaigns.map((campaign) => {
              const isExpanded = expandedCampaigns[campaign.id];
              const insights = campaign.insights || {};
              
              return (
                <Card key={campaign.id} className="overflow-hidden" data-testid={`campaign-${campaign.id}`}>
                  {/* Campaign Level */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-[rgba(255,255,255,0.03)]"
                    onClick={() => toggleCampaignExpand(campaign.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded">
                          <Layers className="w-4 h-4 text-[#FF5C00]" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{campaign.name}</h3>
                            <Badge className={campaign.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-[rgba(245,245,247,0.9)]'}>
                              {campaign.status === 'ACTIVE' ? '🟢 Aktiv' : '⏸️ Pausiert'}
                            </Badge>
                          </div>
                          <p className="text-sm text-[rgba(245,245,247,0.6)]">
                            Kampagne • {campaign.daily_budget || campaign.lifetime_budget || 0}€/Tag
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Quick Stats - Extended */}
                        <div className="hidden lg:flex gap-4 text-center">
                          <div>
                            <p className="text-lg font-semibold">{(insights.reach || 0).toLocaleString()}</p>
                            <p className="text-xs text-[rgba(245,245,247,0.6)]">Reichweite</p>
                          </div>
                          <div>
                            <p className="text-lg font-semibold">{insights.clicks || 0}</p>
                            <p className="text-xs text-[rgba(245,245,247,0.6)]">Klicks</p>
                          </div>
                          <div className={`px-2 py-1 rounded ${getRatingColor(getPerformanceRating('ctr', insights.ctr || 0))}`}>
                            <p className="text-lg font-semibold">{(insights.ctr || 0).toFixed(2)}%</p>
                            <p className="text-xs opacity-80">CTR</p>
                          </div>
                          <div className={`px-2 py-1 rounded ${getRatingColor(getPerformanceRating('cpc', insights.cpc || 0))}`}>
                            <p className="text-lg font-semibold">{(insights.cpc || 0).toFixed(2)}€</p>
                            <p className="text-xs opacity-80">CPC</p>
                          </div>
                          <div>
                            <p className="text-lg font-semibold">{insights.leads || 0}</p>
                            <p className="text-xs text-[rgba(245,245,247,0.6)]">Leads</p>
                          </div>
                          <div className={`px-2 py-1 rounded ${getRatingColor(getPerformanceRating('cost_per_lead', insights.cost_per_lead || 0))}`}>
                            <p className="text-lg font-semibold">{(insights.cost_per_lead || 0).toFixed(2)}€</p>
                            <p className="text-xs opacity-80">CPL</p>
                          </div>
                        </div>
                        
                        {/* Mobile Stats Summary */}
                        <div className="flex lg:hidden gap-2 text-center text-sm">
                          <div className="px-2 py-1 bg-blue-50 rounded">
                            <span className="font-semibold">{(insights.spend || 0).toFixed(0)}€</span>
                          </div>
                          <div className={`px-2 py-1 rounded ${getRatingColor(getPerformanceRating('ctr', insights.ctr || 0))}`}>
                            <span className="font-semibold">{(insights.ctr || 0).toFixed(1)}%</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant={campaign.status === 'ACTIVE' ? 'outline' : 'default'}
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); toggleCampaignStatus(campaign.id, campaign.status); }}
                          >
                            {campaign.status === 'ACTIVE' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          </Button>
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded: Ad Sets */}
                  {isExpanded && campaign.ad_sets && (
                    <div className="border-t bg-[rgba(255,255,255,0.03)]">
                      {campaign.ad_sets.map((adSet) => {
                        const adSetExpanded = expandedAdSets[adSet.id];
                        
                        return (
                          <div key={adSet.id} className="border-b last:border-b-0">
                            {/* Ad Set Level */}
                            <div 
                              className="p-3 pl-12 cursor-pointer hover:bg-gray-100"
                              onClick={() => toggleAdSetExpand(adSet.id)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center justify-center w-6 h-6 bg-blue-100 rounded">
                                    <Users className="w-3 h-3 text-blue-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{adSet.name}</p>
                                    <p className="text-xs text-[rgba(245,245,247,0.6)]">Anzeigengruppe</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge className={adSet.status === 'ACTIVE' ? 'bg-green-100 text-green-700 text-xs' : 'bg-gray-100 text-[rgba(245,245,247,0.9)] text-xs'}>
                                    {adSet.status}
                                  </Badge>
                                  {adSetExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </div>
                              </div>
                            </div>
                            
                            {/* Expanded: Ads */}
                            {adSetExpanded && adSet.ads && (
                              <div className="bg-[rgba(255,255,255,0.05)]">
                                {adSet.ads.map((ad) => (
                                  <div key={ad.id} className="p-3 pl-20 border-t flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center justify-center w-6 h-6 bg-pink-100 rounded">
                                        <FileImage className="w-3 h-3 text-pink-600" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">{ad.name}</p>
                                        <p className="text-xs text-[rgba(245,245,247,0.6)]">Werbeanzeige</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge className={ad.status === 'ACTIVE' ? 'bg-green-100 text-green-700 text-xs' : 'bg-gray-100 text-[rgba(245,245,247,0.9)] text-xs'}>
                                        {ad.status}
                                      </Badge>
                                      <Button variant="ghost" size="sm">
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Spend Summary */}
                  <div className="px-4 py-2 bg-[rgba(255,255,255,0.03)] border-t flex items-center justify-between text-sm">
                    <span className="text-[rgba(245,245,247,0.6)]">Ausgaben (letzte 7 Tage):</span>
                    <span className="font-semibold text-[#FF5C00]">{(insights.spend || 0).toFixed(2)}€</span>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Performance Legend */}
      {campaigns.length > 0 && (
        <Card className="p-4 bg-[rgba(255,255,255,0.03)]">
          <h4 className="font-medium mb-2">Ampel-Bewertung verstehen:</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-green-500"></span>
              <span>Exzellent</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-green-400"></span>
              <span>Gut</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-yellow-500"></span>
              <span>Okay</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-red-500"></span>
              <span>Optimieren</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default MetaAdsAutopilot;
